import type { ClientConfig } from "../api/types";

/** Thrown when /client-config is unreachable or not what the API should return. */
export class ClientConfigError extends Error {
  override name = "ClientConfigError";
}

function isClientConfig(value: unknown): value is ClientConfig {
  if (typeof value !== "object" || value === null) {
    return false;
  }
  const record = value as Record<string, unknown>;
  return (
    typeof record.tenant_id === "string" &&
    typeof record.client_id === "string" &&
    typeof record.api_scope === "string"
  );
}

/**
 * The Entra settings the web app needs before sign-in, read at startup from the
 * API (ADR 0009) so one image serves staging and production.
 */
export async function fetchClientConfig(fetchFn: typeof fetch = fetch): Promise<ClientConfig> {
  let response: Response;
  try {
    response = await fetchFn("/client-config", { headers: { Accept: "application/json" } });
  } catch (error) {
    throw new ClientConfigError("Could not reach the Axon Time server.", { cause: error });
  }
  if (!response.ok) {
    throw new ClientConfigError(`The Axon Time server answered ${String(response.status)}.`);
  }
  const body: unknown = await response.json();
  if (!isClientConfig(body)) {
    throw new ClientConfigError("The Axon Time server sent an unexpected configuration.");
  }
  return body;
}
